import { Button } from '../../../Button';
import { CopyButton } from '../../../CopyButton';
import { Box, HStack } from '../../../Core';
import { Divider } from '../../../Divider';
import { Text } from '../../../Text';
import type { WorkflowSummaryRow, WorkflowSummarySection } from './types';

export function WorkflowSummaryCard({ sections, onCopy }: { sections: WorkflowSummarySection[]; onCopy?: () => void }) {
  return (
    <Box
      borderWidth="1px"
      borderStyle="solid"
      borderColor="colors.gray.030"
      borderRadius="borderRadiusDefault"
      background="backgroundFilledTabList"
      overflow="hidden"
    >
      <Box borderTop="2px solid" borderColor="colors.gray.050">
        {sections.map((section, sectionIndex) => (
          <Box key={section.title}>
            {sectionIndex > 0 ? <Divider mx="spacing16" /> : null}
            <Box px="spacing16" py="spacing16">
              <Box mb="spacing16">
                <Text color="colors.gray.100" fontWeight="500">
                  {section.title}
                </Text>
              </Box>
              <Box display="grid" gap="spacing10">
                {section.rows.map(row => (
                  <HStack
                    key={`${section.title}-${row.label}`}
                    justifyContent="space-between"
                    alignItems="flex-start"
                    gap="spacing16"
                  >
                    <HStack alignItems="flex-start" gap="spacing6">
                      <Text color="colors.gray.080">└</Text>
                      <Text color="colors.gray.080">{row.label}</Text>
                    </HStack>
                    <Box textAlign="right">
                      <Text color="colors.gray.100">{row.value || '--'}</Text>
                    </Box>
                  </HStack>
                ))}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
      {onCopy ? (
        <Box px="spacing16" pb="spacing16">
          <Button width="100%" onClick={onCopy} data-testid="workflow-success-copy-button">
            Copy Details
          </Button>
        </Box>
      ) : null}
    </Box>
  );
}

export function WorkflowCompactSummaryCard({ rows, onCopy }: { rows: WorkflowSummaryRow[]; onCopy?: () => void }) {
  return (
    <Box
      p="spacing16"
      borderWidth="1px"
      borderStyle="solid"
      borderColor="colors.gray.030"
      borderRadius="borderRadiusDefault"
      background="backgroundFilledTabList"
    >
      <HStack justifyContent="space-between" alignItems="center" mb="spacing12">
        <Text fontSize="11px" color="colors.gray.060" textTransform="uppercase" fontWeight="500">
          Summary
        </Text>
        {onCopy ? <CopyButton onClick={onCopy} data-testid="workflow-summary-copy-button" /> : null}
      </HStack>
      <Box display="grid" gap="spacing12">
        {rows.map(row => (
          <HStack key={row.label} justifyContent="space-between" alignItems="flex-start" gap="spacing16">
            <Text color="colors.gray.080" fontWeight="500">
              {row.label}
            </Text>
            <Text color="colors.gray.100" textAlign="right">
              {row.value || '--'}
            </Text>
          </HStack>
        ))}
      </Box>
    </Box>
  );
}
